import React from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
} from '@mui/icons-material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useFieldArray } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

const FIELD_TYPES = [
  {
    value: 'text',
    label: 'Text (Short)',
    icon: '📝',
    category: 'basic',
    description: 'Single line text input',
  },
  {
    value: 'textarea',
    label: 'Text Area (Long)',
    icon: '📄',
    category: 'basic',
    description: 'Multi-line text input',
  },
  {
    value: 'wysiwyg',
    label: 'Rich Text Editor',
    icon: '🔤',
    category: 'basic',
    description: 'HTML formatted text',
  },
  {
    value: 'number',
    label: 'Number',
    icon: '🔢',
    category: 'basic',
    description: 'Numeric input with min/max',
  },
  {
    value: 'email',
    label: 'Email',
    icon: '📧',
    category: 'basic',
    description: 'Email address with validation',
  },
  {
    value: 'image',
    label: 'Single Image',
    icon: '🖼️',
    category: 'media',
    description: 'Single image upload',
  },
  // {
  //   value: 'responsive-image',
  //   label: 'Responsive Image (3 devices)',
  //   icon: '📱💻🖥️',
  //   category: 'media',
  //   description: 'Separate images for mobile, tablet and desktop',
  // },
  {
    value: 'gallery',
    label: 'Image Gallery',
    icon: '🖼️🖼️',
    category: 'media',
    description: 'Multiple images upload',
  },
  {
    value: 'select',
    label: 'Dropdown Select',
    icon: '⬇️',
    category: 'choice',
    description: 'Single selection from dropdown',
  },
  {
    value: 'radio',
    label: 'Radio Button',
    icon: '⚪',
    category: 'choice',
    description: 'Single selection from options',
  },
  {
    value: 'checkbox',
    label: 'Checkbox',
    icon: '✅',
    category: 'choice',
    description: 'Boolean true/false',
  },
  {
    value: 'repeater',
    label: 'Repeater (Repeatable)',
    icon: '🔄',
    category: 'complex',
    description: 'Repeatable group of fields',
  },
  {
    value: 'group',
    label: 'Group (Nested)',
    icon: '📦',
    category: 'complex',
    description: 'Group of related fields',
  },
  {
    value: 'color',
    label: 'Color Picker',
    icon: '🎨',
    category: 'style',
    description: 'Color selection',
  },
  {
    value: 'link',
    label: 'Link/Button',
    icon: '🔗',
    category: 'interactive',
    description: 'Link with text and URL',
  },
];

const NestedFieldBuilder = ({
  fieldPath,
  nestLevel = 1,
  maxDepth = 3,
  control,
  register,
  setValue,
  getValues,
  errors,
}) => {
  const fieldsPrefix = `${fieldPath}.fields`;
  
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: fieldsPrefix,
    keyName: 'hookFormId',
  });

  const handleTypeChange = (fieldIndex, newType) => {
    const currentFields = getValues(fieldsPrefix) || [];
    const updatedFields = [...currentFields];
    
    if (!updatedFields[fieldIndex]) {
      updatedFields[fieldIndex] = {};
    }
    
    updatedFields[fieldIndex] = {
      ...updatedFields[fieldIndex],
      type: newType,
      fields: (newType === 'group' || newType === 'repeater') 
        ? (updatedFields[fieldIndex]?.fields || [])
        : [],
    };
    
    setValue(fieldsPrefix, updatedFields, { 
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    move(result.source.index, result.destination.index);
  };

  const addNestedField = () => {
    append({
      id: `field_${Date.now()}_${Math.random()}_${fields.length}_level${nestLevel}`,
      key: '',
      label: '',
      type: 'text',
      placeholder: '',
      defaultValue: '',
      required: false,
      hidden: false,
      options: [],
      fields: [],
      validation: {},
      condition: null,
      helpText: '',
    });
  };

  return (
    <Box
      sx={{
        ml: nestLevel * 2,
        mt: 2,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1,
        borderLeft: '3px solid',
        borderColor: 'info.main',
      }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <MDTypography variant="body2" color="info" fontWeight="bold">
            {nestLevel === 1 ? 'Nested Fields' : `Level ${nestLevel} Fields`}
          </MDTypography>
          {nestLevel > 1 && (
            <Chip
              label={`Level ${nestLevel}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          startIcon={<Add />}
          onClick={addNestedField}>
          Add Field
        </MDButton>
      </Box>

      {fields.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.100' }}>
          <MDTypography variant="body2" color="textSecondary">
            No fields added yet. Click "Add Field" to add nested fields.
          </MDTypography>
        </Paper>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`nested-fields-${fieldPath}`}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((nestedField, nestedIndex) => (
                  <Draggable
                    key={nestedField.id || nestedField.hookFormId}
                    draggableId={nestedField.id || `field-${nestedIndex}-${Date.now()}`}
                    index={nestedIndex}
                    isDragDisabled={nestLevel > 2}>
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 2,
                          p: 2,
                          position: 'relative',
                          bgcolor: snapshot.isDragging ? 'grey.200' : 'white',
                          borderLeft: nestedField.required ? '3px solid' : 'none',
                          borderLeftColor: 'error.main',
                        }}>
                        
                        {nestLevel <= 2 && (
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              position: 'absolute',
                              left: -10,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              cursor: 'grab',
                              color: 'text.secondary',
                            }}>
                            <DragIndicator />
                          </Box>
                        )}

                        <IconButton
                          size="small"
                          color="error"
                          sx={{ position: 'absolute', right: 8, top: 8 }}
                          onClick={() => remove(nestedIndex)}>
                          <Delete fontSize="small" />
                        </IconButton>

                        <Grid container spacing={2} pr={6}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Field Key"
                              {...register(`${fieldsPrefix}.${nestedIndex}.key`, {
                                required: 'Field key is required',
                                pattern: {
                                  value: /^[a-z0-9_]+$/,
                                  message: 'Only lowercase letters, numbers, underscores',
                                },
                              })}
                              placeholder="field_key"
                              error={!!errors?.fieldDefinitions?.[nestedIndex]?.key}
                            />
                          </Grid>

                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Field Label"
                              {...register(`${fieldsPrefix}.${nestedIndex}.label`, {
                                required: 'Field label is required',
                              })}
                              placeholder="Display Label"
                              error={!!errors?.fieldDefinitions?.[nestedIndex]?.label}
                            />
                          </Grid>

                          <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Type</InputLabel>
                              <Select
                                label="Type"
                                value={nestedField.type || 'text'}
                                onChange={(e) => handleTypeChange(nestedIndex, e.target.value)}>
                                {FIELD_TYPES.filter(t => {
                                  if (t.value === 'repeater' && nestLevel >= maxDepth)
                                    return false;
                                  return true;
                                }).map(type => (
                                  <MenuItem key={type.value} value={type.value}>
                                    <Tooltip title={type.description} placement="right">
                                      <span>
                                        {type.icon} {type.label}
                                      </span>
                                    </Tooltip>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={6} md={2}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={nestedField.required || false}
                                  onChange={(e) => {
                                    const currentFields = getValues(fieldsPrefix) || [];
                                    const updatedFields = [...currentFields];
                                    updatedFields[nestedIndex] = {
                                      ...updatedFields[nestedIndex],
                                      required: e.target.checked,
                                    };
                                    setValue(fieldsPrefix, updatedFields);
                                  }}
                                  size="small"
                                  color="error"
                                />
                              }
                              label="Required"
                            />
                          </Grid>

                          <Grid item xs={6} md={2}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={nestedField.hidden || false}
                                  onChange={(e) => {
                                    const currentFields = getValues(fieldsPrefix) || [];
                                    const updatedFields = [...currentFields];
                                    updatedFields[nestedIndex] = {
                                      ...updatedFields[nestedIndex],
                                      hidden: e.target.checked,
                                    };
                                    setValue(fieldsPrefix, updatedFields);
                                  }}
                                  size="small"
                                />
                              }
                              label="Hidden"
                            />
                          </Grid>
                        </Grid>

                        {/* Nested fields for group/repeater */}
                        {(nestedField.type === 'repeater' || nestedField.type === 'group') &&
                          nestLevel < maxDepth && (
                          <Box sx={{ mt: 2 }}>
                            <NestedFieldBuilder
                              fieldPath={`${fieldsPrefix}.${nestedIndex}`}
                              nestLevel={nestLevel + 1}
                              maxDepth={maxDepth}
                              control={control}
                              register={register}
                              setValue={setValue}
                              getValues={getValues}
                              errors={errors}
                            />
                          </Box>
                        )}
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Box>
  );
};

export default NestedFieldBuilder;
